// src/types/studio.ts
// Types for StrymHub Studios (productions) and the StrymHub Community (the repertory).
// These mirror the backend `presentation.dto.studio` records exactly — camelCase, no aliases.

export const CRAFTS = [
  'ACTOR',
  'VOICE_ARTIST',
  'DIRECTOR',
  'WRITER',
  'PRODUCER',
  'CINEMATOGRAPHER',
  'EDITOR',
  'SOUND',
  'COMPOSER',
  'PRODUCTION_DESIGN',
  'COSTUME',
  'HAIR_MAKEUP',
  'ANIMATOR',
  'ILLUSTRATOR',
  'PHOTOGRAPHER',
  'PRESENTER',
  'STUNTS',
  'CASTING',
  'PRODUCTION_ASSISTANT',
  'OTHER',
] as const;

export type Craft = (typeof CRAFTS)[number];

export const AVAILABILITIES = [
  'OPEN_TO_WORK',
  'SELECTIVELY_AVAILABLE',
  'ON_A_PRODUCTION',
  'UNAVAILABLE',
] as const;

export type Availability = (typeof AVAILABILITIES)[number];

export type MemberStatus = 'PENDING' | 'ACTIVE' | 'HIDDEN' | 'ARCHIVED';

export const PRODUCTION_STAGES = [
  'DEVELOPMENT',
  'PRE_PRODUCTION',
  'IN_PRODUCTION',
  'POST_PRODUCTION',
  'RELEASED',
  'ON_HOLD',
  'ARCHIVED',
] as const;

export type ProductionStage = (typeof PRODUCTION_STAGES)[number];

/**
 * DRAFT or PUBLIC. Public reads never return a draft, so this only ever varies on the
 * studio side - but it decides whether a production's credits appear on a player page,
 * which makes it the single most consequential field on the admin screen.
 */
export const PRODUCTION_VISIBILITIES = ['DRAFT', 'PUBLIC'] as const;

export type ProductionVisibility = (typeof PRODUCTION_VISIBILITIES)[number];

export const PRODUCTION_FORMATS = [
  'SHORT_FILM',
  'FEATURE_FILM',
  'SERIES',
  'ANIMATION',
  'DOCUMENTARY',
  'PODCAST',
  'MUSIC_VIDEO',
  'COMMERCIAL',
  'STAGE_CAPTURE',
  'OTHER',
] as const;

export type ProductionFormat = (typeof PRODUCTION_FORMATS)[number];

export type CallType = 'CAST' | 'CREW' | 'COLLABORATOR';

export type CastingCallStatus = 'DRAFT' | 'OPEN' | 'CLOSED' | 'FILLED';

export type ApplicationStatus =
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'SHORTLISTED'
  | 'AUDITION_INVITED'
  | 'CAST'
  | 'NOT_PROGRESSING'
  | 'WITHDRAWN';

// ---------------------------------------------------------------- community

export interface MemberCredit {
  creditId: string;
  productionId: string;
  productionSlug: string;
  productionTitle: string;
  productionPosterUrl: string | null;
  productionStage: ProductionStage;
  productionYear: number | null;
  roleName: string;
  department: string;
  departmentLabel: string;
  lead: boolean;
}

export interface MemberProfile {
  id: string;
  slug: string;
  displayName: string;
  headline: string | null;
  bio: string | null;
  location: string | null;
  avatarUrl: string | null;
  bannerUrl: string | null;
  showreelUrl: string | null;
  primaryCraft: Craft;
  primaryCraftLabel: string;
  secondaryCrafts: string[];
  skills: string[];
  availability: Availability;
  status: MemberStatus;
  foundingMember: boolean;
  instagramUrl: string | null;
  websiteUrl: string | null;
  imdbUrl: string | null;
  productionCount: number;
  joinedAt: string | null;
  credits: MemberCredit[];
}

export interface MemberProfilePayload {
  displayName: string;
  headline?: string;
  bio?: string;
  location?: string;
  avatarUrl?: string;
  showreelUrl?: string;
  primaryCraft: Craft;
  secondaryCrafts?: string[];
  skills?: string[];
  availability?: Availability;
  instagramUrl?: string;
  websiteUrl?: string;
  imdbUrl?: string;
}

export interface CraftCount {
  craft: Craft;
  label: string;
  count: number;
}

export interface CommunityStats {
  members: number;
  productions: number;
  productionsInProgress: number;
  releasedProductions: number;
  openCalls: number;
  crafts: CraftCount[];
}

// ---------------------------------------------------------------- productions

export interface ProductionSummary {
  id: string;
  slug: string;
  title: string;
  logline: string | null;
  format: ProductionFormat;
  formatLabel: string;
  stage: ProductionStage;
  stageLabel: string;
  visibility: ProductionVisibility;
  genre: string | null;
  posterUrl: string | null;
  shootLocation: string | null;
  productionYear: number | null;
  runtimeMinutes: number | null;
  featured: boolean;
  openForCasting: boolean;
  creditCount: number;
  workCount: number;
}

export interface ProductionCreditEntry {
  id: string;
  creditedName: string;
  roleName: string;
  lead: boolean;
  note: string | null;
  memberSlug: string | null;
  memberAvatarUrl: string | null;
  memberPrimaryCraftLabel: string | null;
}

export interface CreditGroup {
  department: string;
  departmentLabel: string;
  credits: ProductionCreditEntry[];
}

export interface ProductionWork {
  id: string;
  contentId: string;
  contentSlug: string | null;
  title: string;
  description: string | null;
  contentType: string;
  role: string;
  roleLabel: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  watchPath: string;
}

export interface Production {
  id: string;
  slug: string;
  title: string;
  logline: string | null;
  synopsis: string | null;
  format: ProductionFormat;
  formatLabel: string;
  stage: ProductionStage;
  stageLabel: string;
  visibility: ProductionVisibility;
  genre: string | null;
  posterUrl: string | null;
  bannerUrl: string | null;
  trailerUrl: string | null;
  shootLocation: string | null;
  shootStartDate: string | null;
  shootEndDate: string | null;
  releaseDate: string | null;
  productionYear: number | null;
  runtimeMinutes: number | null;
  partnerName: string | null;
  partnerUrl: string | null;
  featured: boolean;
  openForCasting: boolean;
  creditCount: number;
  creditGroups: CreditGroup[];
  works: ProductionWork[];
  openCalls: CastingCallSummary[];
}

// ---------------------------------------------------------------- casting

export interface CastingCallSummary {
  id: string;
  slug: string;
  title: string;
  callType: CallType;
  callTypeLabel: string;
  department: string;
  departmentLabel: string;
  roleName: string | null;
  roleSummary: string | null;
  location: string | null;
  remoteFriendly: boolean;
  paid: boolean;
  compensationSummary: string | null;
  shootDatesSummary: string | null;
  ageRange: string | null;
  submissionDeadline: string | null;
  status: CastingCallStatus;
  acceptingApplications: boolean;
  openings: number;
  bannerUrl: string | null;
  productionSlug: string | null;
  productionTitle: string | null;
}

export interface CastingCall {
  summary: CastingCallSummary;
  description: string | null;
  requirements: string | null;
  production: ProductionSummary | null;
  hasApplied: boolean;
  myApplicationStatus: ApplicationStatus | null;
}

export interface CastingApplicationPayload {
  applicantName: string;
  applicantPhone?: string;
  location?: string;
  coverNote: string;
  reelUrl?: string;
  selfTapeUrl?: string;
  portfolioUrl?: string;
}

export interface CastingApplication {
  id: string;
  castingCallId: string;
  castingCallSlug: string;
  castingCallTitle: string;
  productionTitle: string | null;
  applicantName: string;
  location: string | null;
  coverNote: string;
  reelUrl: string | null;
  selfTapeUrl: string | null;
  portfolioUrl: string | null;
  status: ApplicationStatus;
  statusLabel: string;
  submittedAt: string | null;
}

// ---------------------------------------------------------------- shared

/** Spring Data `Page<T>` as it arrives over the wire. */
export interface PagedResult<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export const EMPTY_PAGE: PagedResult<never> = {
  content: [],
  totalElements: 0,
  totalPages: 0,
  number: 0,
  size: 0,
  first: true,
  last: true,
};
